/**
 * MonitoringV3.jsx - Monitoring Dashboard (Proxmox-Style Layout)
 *
 * Features:
 * - Left sidebar with dialers list and their servers
 * - Main content with tabs: Info | Bots | VMs
 * - First dialer auto-selected on load
 * - Real-time bot status monitoring
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import {
    Phone, Server, HardDrive, Cpu, MemoryStick, RefreshCw, Play, Square, RotateCcw,
    AlertTriangle, CheckCircle, XCircle, Search, ChevronDown, ChevronLeft, ChevronRight, Pause,
    Wifi, WifiOff, Users, Monitor, Info, Bot, PhoneCall, PhoneOff, PhoneForwarded,
    Volume2, Settings, ArrowUpRight, FileText, Clock, Voicemail, Ban, X, Calendar,
    Radio, Edit2, Check, Loader2, ToggleLeft, ToggleRight, Mic, User, ExternalLink,
    BarChart3, Trash2
} from 'lucide-react'
import { monitoringApi, dialerApi, avatarApi, voiceSetApi, licenseApi } from '../services/api'
import DialerBotsTab from '../components/DialerBotsTab'
import DialerStatsTab from '../components/DialerStatsTab'
import CallDetailModal from '../components/CallDetailModal'
import './MonitoringV3.css'
import '../components/CallLogsTab.css'

// Constants
const STALE_THRESHOLD_MINUTES = 2

// Helper: format timestamp as relative time
const formatTimeAgo = (timestamp) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000)
    if (seconds < 60) return 'just now'
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    return `${Math.floor(hours / 24)}d ago`
}

// Searchable Dropdown Component
function SearchableDropdown({ options, value, onChange, placeholder = 'Select...', allLabel = 'All' }) {
    const [isOpen, setIsOpen] = useState(false)
    const [search, setSearch] = useState('')
    const dropdownRef = useRef(null)

    const selectedOption = options.find(opt => String(opt.id) === String(value))

    const filteredOptions = useMemo(() => {
        if (!search) return options
        const q = search.toLowerCase()
        return options.filter(opt => opt.name?.toLowerCase().includes(q))
    }, [options, search])

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false)
                setSearch('')
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    return (
        <div className="searchable-dropdown" ref={dropdownRef}>
            <button
                type="button"
                className={`dropdown-trigger ${isOpen ? 'open' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
            >
                <span>{selectedOption ? selectedOption.name : allLabel}</span>
                <ChevronDown size={14} className={isOpen ? 'rotated' : ''} />
            </button>
            {isOpen && (
                <div className="dropdown-menu">
                    <div className="dropdown-search">
                        <Search size={14} />
                        <input
                            type="text"
                            placeholder={placeholder}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            autoFocus
                        />
                    </div>
                    <div className="dropdown-options">
                        <div className="dropdown-option" onClick={() => { onChange(''); setIsOpen(false); setSearch(''); }}>
                            {allLabel}
                        </div>
                        {filteredOptions.map(opt => (
                            <div
                                key={opt.id}
                                className={`dropdown-option ${String(opt.id) === String(value) ? 'selected' : ''}`}
                                onClick={() => { onChange(opt.id); setIsOpen(false); setSearch(''); }}
                            >
                                {opt.name}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

// Convert SIP IDs to range format: [8001, 8002, 8003, 8005, 8006] -> "8001-8003, 8005-8006"
function formatSipRanges(sipIds) {
    if (!sipIds || sipIds.length === 0) return ''

    // Extract numbers and sort
    const nums = sipIds
        .map(s => parseInt(String(s).replace(/[^0-9]/g, ''), 10))
        .filter(n => !isNaN(n))
        .sort((a, b) => a - b)

    if (nums.length === 0) return ''

    const ranges = []
    let start = nums[0]
    let end = nums[0]

    for (let i = 1; i < nums.length; i++) {
        if (nums[i] === end + 1) {
            end = nums[i]
        } else {
            ranges.push(start === end ? String(start) : `${start}-${end}`)
            start = nums[i]
            end = nums[i]
        }
    }
    ranges.push(start === end ? String(start) : `${start}-${end}`)

    return ranges.join(', ')
}

export default function MonitoringV3() {
    // Core state
    const [loading, setLoading] = useState(true)
    const [dialers, setDialers] = useState([])
    const [servers, setServers] = useState([])
    const [allBots, setAllBots] = useState([])
    const [avatars, setAvatars] = useState([])
    const [voiceSets, setVoiceSets] = useState([])
    const [templates, setTemplates] = useState([])

    // Selection state
    const [selectedDialerId, setSelectedDialerId] = useState(null)
    const [dialerSearch, setDialerSearch] = useState('')
    const [activeTab, setActiveTab] = useState('bots')
    const [filteredSip, setFilteredSip] = useState('')

    // VMs tab state
    const [dialerVMs, setDialerVMs] = useState([])
    const [vmsLoading, setVmsLoading] = useState(false)
    const [vmActionLoading, setVmActionLoading] = useState({})

    // Refresh state - silent auto-refresh every 30s
    const [refreshing, setRefreshing] = useState(false)
    const [lastRefresh, setLastRefresh] = useState(null)

    // Cascade-delete dialer modal
    const [cascadeOpen, setCascadeOpen] = useState(false)

    // Date filters (shared between Bots and Call Logs tabs)
    const [datePreset, setDatePreset] = useState('today')
    const [customFrom, setCustomFrom] = useState('')
    const [customTo, setCustomTo] = useState('')
    const [customTimeFrom, setCustomTimeFrom] = useState('')
    const [customTimeTo, setCustomTimeTo] = useState('')

    // Helper: format a Date as YYYY-MM-DD in LOCAL timezone (not UTC)
    const getDateString = (date) => {
        const pad = (n) => String(n).padStart(2, '0')
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    }

    // Convert a local date + local time (both as strings) into a UTC
    // "YYYY-MM-DD HH:MM" string the backend can match against its UTC-stored
    // call_end column. Falls back to the plain date when no time is given.
    const toUtcDateTimeString = (dateStr, timeStr) => {
        if (!dateStr) return dateStr
        if (!timeStr) return dateStr
        const local = new Date(`${dateStr}T${timeStr}:00`)
        if (Number.isNaN(local.getTime())) return `${dateStr} ${timeStr}`
        const pad = (n) => String(n).padStart(2, '0')
        return `${local.getUTCFullYear()}-${pad(local.getUTCMonth() + 1)}-${pad(local.getUTCDate())} ${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}`
    }

    // For preset pills: convert a local-calendar day (00:00 .. 23:59:59) into
    // UTC datetime strings. Otherwise the backend (UTC) treats the plain
    // "YYYY-MM-DD" as a UTC day boundary, which for UTC+5 users misses up to
    // 5 hours of their actual day — users see "No calls found" on Today.
    const localDayToUtc = (y, m, d, endOfDay = false) => {
        const local = endOfDay
            ? new Date(y, m, d, 23, 59, 59)
            : new Date(y, m, d, 0, 0, 0)
        const pad = (n) => String(n).padStart(2, '0')
        return `${local.getUTCFullYear()}-${pad(local.getUTCMonth() + 1)}-${pad(local.getUTCDate())} ${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}:${pad(local.getUTCSeconds())}`
    }
    const localRangeToUtc = (fromDate, toDate) => ({
        from: localDayToUtc(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate(), false),
        to: localDayToUtc(toDate.getFullYear(), toDate.getMonth(), toDate.getDate(), true)
    })

    // Compute date range from preset
    const dateRange = useMemo(() => {
        const today = new Date()
        switch (datePreset) {
            case 'today':
                return localRangeToUtc(today, today)
            case 'yesterday': {
                const yesterday = new Date(today - 24 * 60 * 60 * 1000)
                return localRangeToUtc(yesterday, yesterday)
            }
            case '7days': {
                const weekAgo = new Date(today - 7 * 24 * 60 * 60 * 1000)
                return localRangeToUtc(weekAgo, today)
            }
            case 'custom': {
                const fromDate = customFrom || getDateString(today)
                const toDate = customTo || getDateString(today)
                return {
                    from: toUtcDateTimeString(fromDate, customTimeFrom),
                    to: toUtcDateTimeString(toDate, customTimeTo)
                }
            }
            default:
                return localRangeToUtc(today, today)
        }
    }, [datePreset, customFrom, customTo, customTimeFrom, customTimeTo])

    // Toast
    const [toast, setToast] = useState(null)

    const showToast = useCallback((message, type = 'info') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }, [])

    // Fetch all data
    const fetchData = useCallback(async (silent = false) => {
        if (!silent) setLoading(true)
        try {
            const [dialersRes, serversRes, botsRes, avatarsRes, voiceSetsRes] = await Promise.allSettled([
                dialerApi.getAll(),
                monitoringApi.getServersStats(),
                monitoringApi.getAllStatus(),
                avatarApi.getAll(),
                voiceSetApi.getAll()
            ])

            if (dialersRes.status === 'fulfilled' && dialersRes.value.data) {
                const activeDialers = (dialersRes.value.data || []).filter(d => !d.archived)
                setDialers(activeDialers)
                // Don't auto-select here - let the useEffect handle it after bots are loaded
            }

            if (serversRes.status === 'fulfilled' && serversRes.value.data) {
                setServers(serversRes.value.data.servers || [])
            }

            if (botsRes.status === 'fulfilled' && botsRes.value.data) {
                const botsData = botsRes.value.data || []
                // Compute effective status for each bot
                const botsWithStatus = botsData.map(bot => ({
                    ...bot,
                    effectiveStatus: computeEffectiveStatus(bot)
                }))
                setAllBots(botsWithStatus)
            }

            if (avatarsRes.status === 'fulfilled' && avatarsRes.value.data) {
                setAvatars(avatarsRes.value.data || [])
            }

            if (voiceSetsRes.status === 'fulfilled' && voiceSetsRes.value.data) {
                setVoiceSets(voiceSetsRes.value.data || [])
            }

            setLastRefresh(new Date())
        } catch (err) {
            console.error('Failed to fetch data:', err)
            if (!silent) showToast('Failed to fetch data', 'error')
        } finally {
            if (!silent) setLoading(false)
        }
    }, [selectedDialerId, showToast])

    // Compute effective status for a bot
    // Returns: 'online', 'waiting', 'offline' (matches backend logic)
    const computeEffectiveStatus = (bot) => {
        // Check API-provided effective_status first (from getBotsByDialer endpoint)
        if (bot.effective_status) {
            const s = bot.effective_status
            if (s === 'active' || s === 'running' || s === 'dial_level_0') return 'online'
            if (s === 'waiting') return 'waiting'
            return 'offline'
        }

        // Fallback: compute from raw fields (matches backend bots-by-dialer logic)
        const lastHeartbeat = bot.last_heartbeat ? new Date(bot.last_heartbeat) : null
        const now = new Date()
        const heartbeatAge = lastHeartbeat ? (now - lastHeartbeat) / 1000 : Infinity

        // Backend uses 60 second threshold for offline
        if (heartbeatAge > 60) return 'offline'

        // Check operational_state (what backend uses)
        if (bot.operational_state === 'running') return 'online'
        if (bot.operational_state === 'dial_level_0') return 'online'
        if (bot.operational_state === 'waiting' || !bot.operational_state) return 'waiting'

        // Fallback to status field if operational_state not available
        if (bot.status === 'online' || bot.status === 'idle' || bot.status === 'active' || bot.status === 'running' || bot.status === 'in_call') return 'online'
        if (bot.status === 'waiting') return 'waiting'
        return 'offline'
    }

    // Initial load
    useEffect(() => {
        fetchData()
    }, [])

    // Auto-refresh every 10 seconds (silent) - matches BotsTab/DialerBotsTab refresh rate
    useEffect(() => {
        const interval = setInterval(() => fetchData(true), 10000)
        return () => clearInterval(interval)
    }, [fetchData])

    // Get selected dialer
    const selectedDialer = useMemo(() => {
        return dialers.find(d => d.id === selectedDialerId) || null
    }, [dialers, selectedDialerId])

    // Get dialer's servers
    const dialerServers = useMemo(() => {
        if (!selectedDialer) return []
        // Find servers that have bots linked to this dialer
        const dialerBots = allBots.filter(b => Number(b.dialer_id) === Number(selectedDialerId))
        const serverIPs = [...new Set(dialerBots.map(b => b.server_ip).filter(Boolean))]
        return servers.filter(s => serverIPs.includes(s.ip_address))
    }, [selectedDialer, allBots, servers, selectedDialerId])

    // Get dialer's bots
    const dialerBots = useMemo(() => {
        return allBots.filter(b => Number(b.dialer_id) === Number(selectedDialerId))
    }, [allBots, selectedDialerId])

    // Bot counts by status
    const botCounts = useMemo(() => {
        const counts = { error: 0, waiting_approval: 0, waiting: 0, offline: 0, in_call: 0, online: 0 }
        dialerBots.forEach(b => {
            if (counts[b.effectiveStatus] !== undefined) counts[b.effectiveStatus]++
        })
        return counts
    }, [dialerBots])

    // Get dialer's avatar
    const dialerAvatar = useMemo(() => {
        if (!selectedDialer?.avatar_id) return null
        return avatars.find(a => a.id === selectedDialer.avatar_id)
    }, [selectedDialer, avatars])

    // Filtered and sorted dialers for sidebar (sorted by active bot count, most active first)
    const filteredDialers = useMemo(() => {
        let result = dialers
        if (dialerSearch) {
            const q = dialerSearch.toLowerCase()
            result = result.filter(d => d.name?.toLowerCase().includes(q))
        }
        // Sort by active bot count (online + in_call) descending, then by total bots
        return result.sort((a, b) => {
            const aBots = allBots.filter(bot => Number(bot.dialer_id) === Number(a.id))
            const bBots = allBots.filter(bot => Number(bot.dialer_id) === Number(b.id))
            const aActive = aBots.filter(bot => bot.effectiveStatus === 'online' || bot.effectiveStatus === 'in_call').length
            const bActive = bBots.filter(bot => bot.effectiveStatus === 'online' || bot.effectiveStatus === 'in_call').length
            // Primary sort: active bots descending
            if (bActive !== aActive) return bActive - aActive
            // Secondary sort: total bots descending
            return bBots.length - aBots.length
        })
    }, [dialers, dialerSearch, allBots])

    // Auto-select top dialer (most bots) when data loads
    useEffect(() => {
        if (!selectedDialerId && filteredDialers.length > 0 && allBots.length > 0) {
            setSelectedDialerId(filteredDialers[0].id)
        }
    }, [filteredDialers, allBots, selectedDialerId])

    // Get dialer issues
    const getDialerIssues = useCallback((dialer) => {
        const issues = []
        const dBots = allBots.filter(b => Number(b.dialer_id) === Number(dialer.id))

        const errorCount = dBots.filter(b => computeEffectiveStatus(b) === 'error').length
        const offlineCount = dBots.filter(b => computeEffectiveStatus(b) === 'offline').length

        if (errorCount > 0) {
            issues.push({ type: 'critical', message: `${errorCount} bot${errorCount > 1 ? 's' : ''} in error state` })
        }
        if (offlineCount > dBots.length * 0.5 && dBots.length > 0) {
            issues.push({ type: 'warning', message: `${Math.round(offlineCount / dBots.length * 100)}% bots offline` })
        }

        return issues
    }, [allBots])

    // Get server status lights color
    const getStatusLightColor = (value) => {
        if (value >= 90) return 'red'
        if (value >= 60) return 'yellow'
        return 'green'
    }

    // Fetch VMs for dialer when switching to VMs tab
    const fetchDialerVMs = useCallback(async () => {
        if (!selectedDialerId || dialerServers.length === 0) {
            setDialerVMs([])
            return
        }
        setVmsLoading(true)
        try {
            const vms = []
            for (const server of dialerServers) {
                const res = await monitoringApi.getServerVMs(server.ip_address)
                if (res.data.success) {
                    const serverVMs = (res.data.vms || []).filter(vm => {
                        // Only include VMs linked to this dialer
                        const linkedBot = dialerBots.find(b => b.vmid === vm.vmid && b.server_ip === server.ip_address)
                        return linkedBot !== undefined
                    }).map(vm => ({
                        ...vm,
                        server_ip: server.ip_address,
                        server_name: server.name
                    }))
                    vms.push(...serverVMs)
                }
            }
            setDialerVMs(vms)
        } catch (err) {
            console.error('Failed to fetch VMs:', err)
            showToast('Failed to fetch VMs', 'error')
        } finally {
            setVmsLoading(false)
        }
    }, [selectedDialerId, dialerServers, dialerBots, showToast])

    // Handle tab change
    const handleTabChange = (tab) => {
        setActiveTab(tab)
        if (tab === 'vms' && dialerVMs.length === 0) {
            fetchDialerVMs()
        }
    }

    // VM actions
    const handleVMAction = async (vm, action) => {
        setVmActionLoading(prev => ({ ...prev, [vm.vmid]: action }))
        try {
            // Find linked bot to get sip_id and dialer_id
            const linkedBot = dialerBots.find(b => b.vmid === vm.vmid && b.server_ip === vm.server_ip)
            await monitoringApi.vmAction(vm.server_ip, vm.vmid, action, linkedBot?.sip_id, linkedBot?.dialer_id)
            showToast(`${action} command sent to VM ${vm.vmid}`, 'success')
            setTimeout(() => fetchDialerVMs(), 3000)
        } catch (err) {
            showToast(`VM ${action} failed: ${err.message}`, 'error')
        } finally {
            setVmActionLoading(prev => ({ ...prev, [vm.vmid]: null }))
        }
    }

    // Time ago helper
    const timeAgo = (date) => {
        if (!date) return 'Never'
        const seconds = Math.abs(Math.floor((new Date() - new Date(date)) / 1000))
        if (seconds < 60) return `${seconds}s ago`
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
        if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
        return `${Math.floor(seconds / 86400)}d ago`
    }

    // Loading state
    if (loading) {
        return (
            <div className="monitoring-page-v3">
                <div className="loading-state">
                    <RefreshCw className="spin" size={32} />
                    <p>Loading monitoring data...</p>
                </div>
            </div>
        )
    }

    return (
        <div className="monitoring-page-v3">
            {/* Toast */}
            {toast && (
                <div className={`toast toast-${toast.type}`}>
                    {toast.type === 'success' && <CheckCircle size={16} />}
                    {toast.type === 'error' && <XCircle size={16} />}
                    {toast.type === 'warning' && <AlertTriangle size={16} />}
                    <span>{toast.message}</span>
                </div>
            )}

            {/* Main Layout */}
            <div className="monitoring-layout">
                {/* Dialer Sidebar - Modern Corporate Style */}
                <aside className="dialer-sidebar-v2">
                    <div className="sidebar-header-v2">
                        <div className="sidebar-title-row">
                            <h3>Dialers <span className="count">{dialers.length}</span></h3>
                        </div>
                        <div className="sidebar-search">
                            <Search size={14} className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search dialers..."
                                value={dialerSearch}
                                onChange={(e) => setDialerSearch(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="dialer-list-v2">
                        {filteredDialers.map(dialer => {
                            const isSelected = dialer.id === selectedDialerId
                            // Use Number() to ensure type consistency for comparison
                            const dialerIdNum = Number(dialer.id)
                            const dBots = allBots.filter(b => Number(b.dialer_id) === dialerIdNum)
                            const botCount = dBots.length
                            const issues = getDialerIssues(dialer)
                            const hasCritical = issues.some(i => i.type === 'critical')

                            // Count bots by status
                            const onlineCount = dBots.filter(b => {
                                const s = computeEffectiveStatus(b)
                                return s === 'online' || s === 'in_call'
                            }).length
                            const waitingCount = dBots.filter(b => computeEffectiveStatus(b) === 'waiting').length
                            const offlineCount = dBots.filter(b => computeEffectiveStatus(b) === 'offline').length
                            const errorCount = dBots.filter(b => computeEffectiveStatus(b) === 'error').length

                            // Additional computed values
                            const vmConnected = dBots.filter(b => b.vmid).length
                            const lastHeartbeat = dBots.reduce((latest, b) => {
                                if (!b.last_heartbeat) return latest
                                const t = new Date(b.last_heartbeat).getTime()
                                return t > latest ? t : latest
                            }, 0)
                            const lastSeenText = lastHeartbeat ? formatTimeAgo(lastHeartbeat) : 'Never'
                            const onlinePercent = botCount > 0 ? (onlineCount / botCount) * 100 : 0

                            // Determine status (for dot)
                            let status = 'offline'
                            if (hasCritical || errorCount > 0) status = 'error'
                            else if (onlineCount === botCount && botCount > 0) status = 'online'
                            else if (onlineCount > 0) status = 'partial'
                            else if (waitingCount > 0) status = 'waiting'

                            // Border status (more granular)
                            let borderStatus = 'offline'
                            if (errorCount > 0) borderStatus = 'error'
                            else if (onlineCount === botCount && botCount > 0) borderStatus = 'online'
                            else if (onlineCount > 0 && offlineCount > 0) borderStatus = 'partial'
                            else if (onlineCount > 0 || waitingCount > 0) borderStatus = 'waiting'

                            return (
                                <div
                                    key={dialer.id}
                                    className={`dialer-item-v2 ${isSelected ? 'selected' : ''} border-${borderStatus}`}
                                    onClick={() => {
                                        setSelectedDialerId(dialer.id)
                                        setActiveTab('bots')
                                        setDialerVMs([])
                                    }}
                                >
                                    <div className="dialer-card-body">
                                        <div className="dialer-card-info">
                                            <div className="dialer-card-header">
                                                <span className={`status-indicator ${status}`} />
                                                <span className="dialer-name">{dialer.name}</span>
                                            </div>
                                            <div className="dialer-card-meta">
                                                Last seen: {lastSeenText}
                                            </div>
                                            <div className="dialer-card-stats">
                                                <span className="mini-stat online" title="Online">
                                                    <span className="mini-dot online" />{onlineCount}
                                                </span>
                                                <span className="mini-stat waiting" title="Logged Out">
                                                    <span className="mini-dot waiting" />{waitingCount}
                                                </span>
                                                <span className="mini-stat offline" title="Offline">
                                                    <span className="mini-dot offline" />{offlineCount}
                                                </span>
                                                <span className="mini-stat vms" title="VMs Connected">
                                                    <Monitor size={10} />{vmConnected}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="dialer-card-count">
                                            <span className="count-big">{onlineCount}</span>
                                            <span className="count-total">/{botCount}</span>
                                        </div>
                                    </div>
                                    <div className="dialer-progress-bar">
                                        <div className="dialer-progress-fill" style={{ width: `${onlinePercent}%` }} />
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </aside>

                {/* Main Content */}
                <div className="dialer-content">
                    {selectedDialer ? (
                        <>
                            {/* Content Header */}
                            <div className="content-header">
                                <div className="content-header-left">
                                    <h2>{selectedDialer.name}</h2>
                                    <span className={`status-badge-sm ${selectedDialer.active !== false ? 'online' : 'offline'}`}>
                                        {selectedDialer.active !== false ? 'Active' : 'Inactive'}
                                    </span>
                                    <div className="content-tabs">
                                        <button
                                            className={`content-tab ${activeTab === 'info' ? 'active' : ''}`}
                                            onClick={() => handleTabChange('info')}
                                        >
                                            <Info size={12} />
                                            Info
                                        </button>
                                        <button
                                            className={`content-tab ${activeTab === 'bots' ? 'active' : ''}`}
                                            onClick={() => handleTabChange('bots')}
                                        >
                                            <Bot size={12} />
                                            Bots ({dialerBots.length})
                                        </button>
                                        <button
                                            className={`content-tab ${activeTab === 'vms' ? 'active' : ''}`}
                                            onClick={() => handleTabChange('vms')}
                                        >
                                            <Monitor size={12} />
                                            VMs
                                        </button>
                                        <button
                                            className={`content-tab ${activeTab === 'logs' ? 'active' : ''}`}
                                            onClick={() => handleTabChange('logs')}
                                        >
                                            <FileText size={12} />
                                            Call Logs
                                        </button>
                                        <button
                                            className={`content-tab ${activeTab === 'stats' ? 'active' : ''}`}
                                            onClick={() => handleTabChange('stats')}
                                        >
                                            <BarChart3 size={12} />
                                            Stats
                                        </button>
                                    </div>
                                </div>
                                <div className="content-header-right">
                                    {/* Date Filter Pills - hidden when Stats tab is active (it has its own) */}
                                    {activeTab !== 'stats' && <>
                                    <div className="date-filter-wrapper">
                                        <div className="date-filter-pills">
                                            {[
                                                { key: 'today', label: 'Today' },
                                                { key: 'yesterday', label: 'Yesterday' },
                                                { key: '7days', label: '7 Days' },
                                                { key: 'custom', label: 'Custom' }
                                            ].map(p => (
                                                <button
                                                    key={p.key}
                                                    className={`date-pill ${datePreset === p.key ? 'active' : ''}`}
                                                    onClick={() => setDatePreset(p.key)}
                                                >
                                                    {p.label}
                                                </button>
                                            ))}
                                        </div>
                                        {datePreset === 'custom' && (
                                            <div className="date-custom-inputs">
                                                <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} />
                                                <input type="time" value={customTimeFrom || ''} onChange={e => setCustomTimeFrom(e.target.value)} title="Start time" />
                                                <span>-</span>
                                                <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} />
                                                <input type="time" value={customTimeTo || ''} onChange={e => setCustomTimeTo(e.target.value)} title="End time" />
                                            </div>
                                        )}
                                    </div>
                                    <button className="btn-icon" onClick={() => fetchData()} disabled={refreshing} title="Refresh">
                                        <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
                                    </button>
                                    <button
                                      className="btn-icon"
                                      onClick={() => setCascadeOpen(true)}
                                      title="Delete dialer (with optional templates / avatars)"
                                      style={{ color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)' }}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                    </>}
                                </div>
                            </div>

                            {/* Tab Content */}
                            {activeTab === 'info' && (
                                <DialerInfoTab
                                    dialer={selectedDialer}
                                    avatar={dialerAvatar}
                                    avatars={avatars}
                                    voiceSets={voiceSets}
                                    servers={dialerServers}
                                    bots={dialerBots}
                                    botCounts={botCounts}
                                    issues={getDialerIssues(selectedDialer)}
                                    timeAgo={timeAgo}
                                    getStatusLightColor={getStatusLightColor}
                                    showToast={showToast}
                                    onRefresh={() => fetchData(true)}
                                    dateFrom={dateRange.from}
                                    dateTo={dateRange.to}
                                />
                            )}

                            {activeTab === 'bots' && (
                                <DialerBotsTab
                                    dialerId={selectedDialerId}
                                    dialerName={selectedDialer?.name}
                                    dateFrom={dateRange.from}
                                    dateTo={dateRange.to}
                                    onRefresh={() => fetchData(true)}
                                    onTabChange={(tab) => setActiveTab(tab)}
                                    onFilterSip={(sip) => {
                                        setFilteredSip(sip)
                                        // Expand date range to 7 days when filtering by specific SIP
                                        setDatePreset('7days')
                                    }}
                                />
                            )}

                            {activeTab === 'vms' && (
                                <VMsTab
                                    vms={dialerVMs}
                                    loading={vmsLoading}
                                    dialerBots={dialerBots}
                                    vmActionLoading={vmActionLoading}
                                    onVMAction={handleVMAction}
                                    onRefresh={fetchDialerVMs}
                                    timeAgo={timeAgo}
                                />
                            )}

                            {activeTab === 'logs' && (
                                <DialerCallLogsTab
                                    dialerId={selectedDialerId}
                                    dialerName={selectedDialer?.name}
                                    dialerBots={dialerBots}
                                    initialSipFilter={filteredSip}
                                    onClearFilter={() => setFilteredSip('')}
                                    dateFrom={dateRange.from}
                                    dateTo={dateRange.to}
                                />
                            )}

                            {activeTab === 'stats' && (
                                <DialerStatsTab
                                    dialerId={selectedDialerId}
                                    dialerName={selectedDialer?.name}
                                />
                            )}
                        </>
                    ) : (
                        <div className="no-dialer-selected">
                            <Phone size={48} />
                            <p>Select a dialer from the sidebar</p>
                        </div>
                    )}
                </div>
            </div>

            {cascadeOpen && selectedDialer && (
              <CascadeDeleteDialerModal
                dialer={selectedDialer}
                onClose={() => setCascadeOpen(false)}
                onDeleted={() => {
                  setCascadeOpen(false)
                  setSelectedDialerId(null)
                  fetchData()
                  showToast('Dialer deleted', 'success')
                }}
                showToast={showToast}
              />
            )}
        </div>
    )
}

// Cascade-delete dialer modal — shows preview counts, lets user opt into
// avatar deletion (with shared-warning), requires typing dialer name to confirm.
function CascadeDeleteDialerModal({ dialer, onClose, onDeleted, showToast }) {
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(false)
  const [deleteAvatars, setDeleteAvatars] = useState(false)
  const [deleteCallHistory, setDeleteCallHistory] = useState(false)
  const [confirmName, setConfirmName] = useState('')

  useEffect(() => {
    let mounted = true
    dialerApi.cascadePreview(dialer.id)
      .then(r => { if (mounted) { setPreview(r.data); setLoading(false) } })
      .catch(e => {
        if (!mounted) return
        showToast('Preview failed: ' + (e.response?.data?.error || e.message), 'error')
        onClose()
      })
    return () => { mounted = false }
  }, [dialer.id])

  const canDelete = !deleting && confirmName === dialer.name

  const onDelete = async () => {
    if (!canDelete) return
    setDeleting(true)
    try {
      const res = await dialerApi.cascadeDelete(dialer.id, {
        delete_avatars: deleteAvatars,
        delete_call_history: deleteCallHistory,
        confirm_name: confirmName,
        user_name: 'dashboard'
      })
      console.log('[CascadeDelete] summary:', res.data?.summary)
      onDeleted()
    } catch (e) {
      showToast('Delete failed: ' + (e.response?.data?.error || e.message), 'error')
      setDeleting(false)
    }
  }

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
  }
  const card = {
    background: 'var(--bg-card)', border: '1px solid var(--border-color)',
    borderRadius: '10px', width: 'min(560px, 92vw)', maxHeight: '90vh', overflow: 'auto',
    padding: '20px 22px'
  }
  const row = (label, val, danger=false) => (
    <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', fontSize:'0.86rem', borderBottom:'1px dashed var(--border-color)' }}>
      <span style={{ color:'var(--text-muted)' }}>{label}</span>
      <span style={{ fontWeight:600, color: danger ? '#ef4444' : 'var(--text-primary)' }}>{val}</span>
    </div>
  )

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'14px' }}>
          <h2 style={{ margin:0, fontSize:'1.1rem', display:'flex', alignItems:'center', gap:'8px' }}>
            <Trash2 size={18} style={{ color:'#ef4444' }} />
            Delete dialer: {dialer.name}
          </h2>
          <button onClick={onClose} className="btn-icon"><X size={14} /></button>
        </div>

        {loading ? (
          <div style={{ padding:'30px', textAlign:'center', color:'var(--text-muted)' }}>
            <Loader2 size={18} className="spin" /> Loading…
          </div>
        ) : !preview ? null : (
          <>
            <div style={{ background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'8px', padding:'10px 12px', marginBottom:'14px', fontSize:'0.85rem' }}>
              ⚠️ This permanently deletes the dialer and the data below. Cannot be undone.
            </div>

            <div style={{ marginBottom:'14px' }}>
              <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:'6px' }}>
                Will be deleted automatically (FK-bound)
              </div>
              {row('Bots (bot_status rows)', preview.bots.count)}
              {row('Licenses', preview.licenses.count)}
              {row('Machines (VM records)', preview.machines.count)}
              {row('Templates', preview.templates.count)}
            </div>

            <div style={{ marginBottom:'10px' }}>
              <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:'6px' }}>
                Optional
              </div>

              <label style={{ display:'flex', gap:'10px', padding:'10px', border:'1px solid var(--border-color)', borderRadius:'8px', cursor:'pointer', marginBottom:'8px' }}>
                <input type="checkbox" checked={deleteAvatars} onChange={e => setDeleteAvatars(e.target.checked)} style={{ marginTop:'3px' }} />
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:'0.9rem' }}>
                    Delete linked avatars ({preview.avatars.exclusive_count} exclusive of {preview.avatars.count})
                  </div>
                  <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginTop:'4px' }}>
                    Only avatars NOT used by other dialers will be deleted.
                    {preview.avatars.shared_count > 0 && (
                      <span style={{ color:'#f59e0b' }}> {preview.avatars.shared_count} shared avatar(s) will be skipped.</span>
                    )}
                  </div>
                  {deleteAvatars && preview.avatars.items.length > 0 && (
                    <div style={{ marginTop:'8px', fontSize:'0.78rem' }}>
                      {preview.avatars.items.map(a => (
                        <div key={a.id} style={{ padding:'2px 0', color: a.shared ? '#f59e0b' : '#22c55e' }}>
                          {a.shared ? '⊘' : '✓'} {a.name}
                          {a.shared && <span style={{ marginLeft:'6px', color:'var(--text-muted)' }}>(shared with {a.shared_with_dialers} other)</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </label>

              <label style={{ display:'flex', gap:'10px', padding:'10px', border:'1px solid var(--border-color)', borderRadius:'8px', cursor:'pointer' }}>
                <input type="checkbox" checked={deleteCallHistory} onChange={e => setDeleteCallHistory(e.target.checked)} style={{ marginTop:'3px' }} />
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:'0.9rem' }}>
                    Delete call history ({preview.call_dispositions.count.toLocaleString()} call records + events)
                  </div>
                  <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginTop:'4px' }}>
                    Default off. Keep history if you may need stats / recordings later.
                  </div>
                </div>
              </label>
            </div>

            <div style={{ marginTop:'14px' }}>
              <div style={{ fontSize:'0.82rem', marginBottom:'4px' }}>
                Type <strong style={{ color:'#ef4444' }}>{dialer.name}</strong> to confirm:
              </div>
              <input
                type="text"
                value={confirmName}
                onChange={e => setConfirmName(e.target.value)}
                placeholder={dialer.name}
                style={{
                  width:'100%', padding:'8px 10px', border:'1px solid var(--border-color)',
                  borderRadius:'6px', background:'var(--bg-elevated)', color:'var(--text-primary)',
                  fontSize:'0.9rem'
                }}
                disabled={deleting}
              />
            </div>

            <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end', marginTop:'18px' }}>
              <button className="btn btn-secondary" onClick={onClose} disabled={deleting}>Cancel</button>
              <button
                className="btn"
                disabled={!canDelete}
                onClick={onDelete}
                style={{
                  background: canDelete ? '#ef4444' : 'rgba(239,68,68,0.3)',
                  color: '#fff',
                  cursor: canDelete ? 'pointer' : 'not-allowed'
                }}
              >
                {deleting ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />}
                {deleting ? 'Deleting…' : 'Delete dialer'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// Dialer Info Tab Component - Modern Redesign
function DialerInfoTab({ dialer, avatar, avatars, voiceSets, servers, bots, botCounts, issues, timeAgo, getStatusLightColor, showToast, onRefresh, dateFrom, dateTo }) {
    // Modal states
    const [showAvatarModal, setShowAvatarModal] = useState(false)
    const [showVoiceSetModal, setShowVoiceSetModal] = useState(false)
    const [showTranscriberModal, setShowTranscriberModal] = useState(false)
    const [saving, setSaving] = useState(false)

    // Auto logout timer state
    const [autoLogoutTime, setAutoLogoutTime] = useState(dialer?.auto_logout_time || '')
    const [autoLogoutSaving, setAutoLogoutSaving] = useState(false)

    // License data (for voice pool and transcriber)
    const [licenseData, setLicenseData] = useState(null)
    const [licenseLoading, setLicenseLoading] = useState(false)

    // Stats based on date range (null = not loaded yet)
    const [dateStats, setDateStats] = useState(null)
    const [statsLoading, setStatsLoading] = useState(false)

    // Transcriber options (matching BotsTab)
    const TRANSCRIBER_OPTIONS = [
        { value: 'VOSK', label: 'VOSK', description: 'Fast, local processing' },
        { value: 'HYBRID', label: 'Hybrid', description: 'VOSK + Whisper for accuracy' },
        { value: 'WHISPER_TINY', label: 'Whisper Tiny', description: 'Cloud, fast' },
        { value: 'WHISPER_BASE', label: 'Whisper Base', description: 'Cloud, balanced' }
    ]

    // Get primary license ID from first bot (all bots in dialer share same license config)
    const primaryLicenseId = useMemo(() => {
        const botWithLicense = bots.find(b => b.license_id)
        return botWithLicense?.license_id
    }, [bots])

    // Fetch license data when dialer changes
    useEffect(() => {
        const fetchLicenseData = async () => {
            if (!primaryLicenseId) return
            setLicenseLoading(true)
            try {
                const res = await monitoringApi.getLicenseVoicePool(primaryLicenseId)
                if (res.data?.success) {
                    setLicenseData(res.data.license)
                }
            } catch (err) {
                console.error('Failed to fetch license data:', err)
            } finally {
                setLicenseLoading(false)
            }
        }
        fetchLicenseData()
    }, [primaryLicenseId])

    // Sync auto logout time when dialer changes
    useEffect(() => {
        setAutoLogoutTime(dialer?.auto_logout_time || '')
    }, [dialer?.id, dialer?.auto_logout_time])

    // Fetch stats based on date range
    useEffect(() => {
        const fetchDateStats = async () => {
            if (!dialer?.id || !dateFrom || !dateTo) return
            setDateStats(null) // Reset while fetching new stats
            setStatsLoading(true)
            try {
                const res = await monitoringApi.getCallLogs({
                    dialer_id: dialer.id,
                    from: dateFrom,
                    to: dateTo,
                    limit: 1
                })
                if (res.data?.success && res.data?.data) {
                    const totalCalls = res.data.data.pagination?.total || 0
                    // Sum transfers from breakdown
                    const transfers = (res.data.data.breakdown || []).reduce(
                        (sum, d) => sum + (d.transferred_count || 0), 0
                    )
                    setDateStats({ calls: totalCalls, transfers })
                }
            } catch (err) {
                console.error('Failed to fetch date stats:', err)
                setDateStats({ calls: 0, transfers: 0 }) // Set to 0 on error
            } finally {
                setStatsLoading(false)
            }
        }
        fetchDateStats()
    }, [dialer?.id, dateFrom, dateTo])

    // Get voice sets in use by this dialer
    const voiceSetsInUse = useMemo(() => {
        if (!licenseData?.voice_set_pool || !voiceSets.length) return []
        return voiceSets.filter(vs => licenseData.voice_set_pool.includes(vs.id))
    }, [licenseData, voiceSets])

    // Calculate stats - use date stats when loaded, otherwise fallback to bot data (for initial render)
    const totalCalls = dateStats !== null ? dateStats.calls : bots.reduce((sum, b) => sum + (b.calls_today || 0), 0)
    const totalTransfers = dateStats !== null ? dateStats.transfers : bots.reduce((sum, b) => sum + (b.transfers_today || 0), 0)
    const transferRate = totalCalls > 0 ? Math.round((totalTransfers / totalCalls) * 100) : 0

    // Get unique transcribers from all bots
    const uniqueTranscribers = useMemo(() => {
        const transcriberMap = {}
        bots.forEach(bot => {
            const t = bot.transcriber_type || bot.transcriber || 'VOSK'
            if (!transcriberMap[t]) transcriberMap[t] = []
            transcriberMap[t].push(bot.sip_id)
        })
        return transcriberMap
    }, [bots])

    // Get unique avatars from all bots
    const uniqueAvatars = useMemo(() => {
        const avatarMap = {}
        bots.forEach(bot => {
            if (bot.avatar_id) {
                if (!avatarMap[bot.avatar_id]) avatarMap[bot.avatar_id] = []
                avatarMap[bot.avatar_id].push(bot.sip_id)
            }
        })
        return Object.entries(avatarMap).map(([id, sips]) => ({
            avatar: avatars.find(a => String(a.id) === String(id)),
            botCount: sips.length
        })).filter(a => a.avatar)
    }, [bots, avatars])

    // Get current transcriber (most common among bots)
    const currentTranscriber = useMemo(() => {
        const entries = Object.entries(uniqueTranscribers)
        if (entries.length === 0) return 'VOSK'
        return entries.sort((a, b) => b[1].length - a[1].length)[0][0]
    }, [uniqueTranscribers])

    // Save avatar change
    const handleAvatarChange = async (avatarId) => {
        setSaving(true)
        try {
            await dialerApi.update(dialer.id, { avatar_id: avatarId })
            showToast('Avatar updated successfully', 'success')
            setShowAvatarModal(false)
            onRefresh()
        } catch (err) {
            showToast('Failed to update avatar', 'error')
        } finally {
            setSaving(false)
        }
    }

    // Save voice set pool change
    const handleVoiceSetChange = async (selectedIds) => {
        if (!primaryLicenseId) {
            showToast('No license found', 'error')
            return
        }
        setSaving(true)
        try {
            await monitoringApi.updateLicenseVoicePool(primaryLicenseId, selectedIds)
            setLicenseData(prev => ({ ...prev, voice_set_pool: selectedIds }))
            showToast('Voice sets updated. Restart VMs to apply.', 'success')
            setShowVoiceSetModal(false)
        } catch (err) {
            showToast('Failed to update voice sets', 'error')
        } finally {
            setSaving(false)
        }
    }

    // Save transcriber change
    const handleTranscriberChange = async (transcriber) => {
        if (!primaryLicenseId) {
            showToast('No license found', 'error')
            return
        }
        setSaving(true)
        try {
            await monitoringApi.updateLicenseTranscriber(primaryLicenseId, transcriber)
            setLicenseData(prev => ({ ...prev, transcriber }))
            showToast('Transcriber updated. Restart VMs to apply.', 'success')
            setShowTranscriberModal(false)
        } catch (err) {
            showToast('Failed to update transcriber', 'error')
        } finally {
            setSaving(false)
        }
    }

    // Save auto logout time
    const handleAutoLogoutSave = async () => {
        setAutoLogoutSaving(true)
        try {
            await dialerApi.update(dialer.id, { auto_logout_time: autoLogoutTime || null })
            showToast(autoLogoutTime ? `Auto logout set to ${autoLogoutTime} PKT` : 'Auto logout disabled', 'success')
            onRefresh()
        } catch (err) {
            showToast('Failed to update auto logout time', 'error')
        } finally {
            setAutoLogoutSaving(false)
        }
    }

    // Get current PKT time for display
    const getCurrentPKT = () => {
        const now = new Date()
        const utcHours = now.getUTCHours()
        const utcMinutes = now.getUTCMinutes()
        let pktHours = utcHours + 5
        if (pktHours >= 24) pktHours -= 24
        return `${String(pktHours).padStart(2, '0')}:${String(utcMinutes).padStart(2, '0')}`
    }

    return (
        <div className="dit-container">
            {/* Issues Alert - Compact */}
            {issues.length > 0 && (
                <div className="dit-issues-compact">
                    <AlertTriangle size={14} />
                    <span className="dit-issues-text">
                        {issues.length} issue{issues.length > 1 ? 's' : ''}: {issues.map(i => i.message).join(' • ')}
                    </span>
                </div>
            )}

            {/* Stats Row */}
            <div className="dit-stats">
                <div className="dit-stat">
                    <div className="dit-stat-icon bots"><Bot size={16} /></div>
                    <div className="dit-stat-info">
                        <span className="dit-stat-value">{bots.length}</span>
                        <span className="dit-stat-label">Bots</span>
                    </div>
                    <div className="dit-stat-sub">
                        {(botCounts.online + botCounts.in_call) > 0 && <span className="online">{botCounts.online + botCounts.in_call} online</span>}
                        {botCounts.waiting > 0 && <span className="waiting">{botCounts.waiting} logged out</span>}
                        {botCounts.waiting_approval > 0 && <span className="pending">{botCounts.waiting_approval} pending</span>}
                        {botCounts.offline > 0 && <span className="offline">{botCounts.offline} offline</span>}
                        {botCounts.error > 0 && <span className="error">{botCounts.error} error</span>}
                    </div>
                </div>

                <div className="dit-stat">
                    <div className="dit-stat-icon calls"><PhoneCall size={16} /></div>
                    <div className="dit-stat-info">
                        <span className="dit-stat-value">{statsLoading ? '...' : totalCalls.toLocaleString()}</span>
                        <span className="dit-stat-label">Calls</span>
                    </div>
                    <div className="dit-stat-sub">{botCounts.in_call} in call</div>
                </div>

                <div className="dit-stat">
                    <div className="dit-stat-icon transfers"><ArrowUpRight size={16} /></div>
                    <div className="dit-stat-info">
                        <span className="dit-stat-value">{statsLoading ? '...' : totalTransfers}</span>
                        <span className="dit-stat-label">Transfers</span>
                    </div>
                    <div className="dit-stat-sub">{transferRate}% rate</div>
                </div>

                <div className="dit-stat">
                    <div className="dit-stat-icon servers"><Server size={16} /></div>
                    <div className="dit-stat-info">
                        <span className="dit-stat-value">{servers.length}</span>
                        <span className="dit-stat-label">Servers</span>
                    </div>
                    <div className="dit-stat-sub">{servers.filter(s => s.online).length} online</div>
                </div>
            </div>

            {/* Servers Section */}
            {servers.length > 0 && (
                <div className="dit-servers">
                    <div className="dit-section-header">
                        <Server size={16} />
                        <span>Servers</span>
                        <span className="server-count">{servers.length}</span>
                    </div>
                    <div className="dit-servers-list">
                        {servers.map(server => {
                            const ramPercent = server.ram_total_gb ? Math.round((server.ram_used_gb / server.ram_total_gb) * 100) : 0
                            const diskPercent = server.disk_total_gb ? Math.round((server.disk_used_gb / server.disk_total_gb) * 100) : 0
                            const serverBots = bots.filter(b => b.server_ip === server.ip_address)
                            const vmCount = serverBots.length
                            const sipRanges = formatSipRanges(serverBots.map(b => b.sip_id))

                            return (
                                <div key={server.id} className="dit-server">
                                    <div className="dit-server-header">
                                        <div className="dit-server-info">
                                            <span className={`dit-server-dot ${server.online ? 'online' : 'offline'}`} />
                                            <span className="dit-server-name">{server.name || 'Server'}</span>
                                        </div>
                                        <a
                                            href={`https://${server.ip_address}:8006`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="dit-server-ip proxmox-link"
                                            title="Open Proxmox Console"
                                        >
                                            {server.ip_address}
                                            <ExternalLink size={11} />
                                        </a>
                                        <span className="dit-vm-badge">
                                            <Monitor size={12} />
                                            {vmCount} VMs
                                        </span>
                                    </div>
                                    {sipRanges && (
                                        <div className="dit-server-sips">
                                            <span className="dit-sip-list" title={serverBots.map(b => b.sip_id).join(', ')}>
                                                <span className="dit-sip-label">SIPs:</span>
                                                {sipRanges}
                                            </span>
                                        </div>
                                    )}
                                    <div className="dit-server-metrics">
                                        <span className={`dit-metric ${getStatusLightColor(server.cpu_usage || 0)}`}>
                                            <Cpu size={13} /> {server.cpu_usage || 0}%
                                        </span>
                                        <span className={`dit-metric ${getStatusLightColor(ramPercent)}`}>
                                            <MemoryStick size={13} /> {ramPercent}%
                                        </span>
                                        <span className={`dit-metric ${getStatusLightColor(diskPercent)}`}>
                                            <HardDrive size={13} /> {diskPercent}%
                                        </span>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}

            {/* Configuration Grid */}
            <div className="dit-config-grid">
                {/* Left Column - Bot Configuration */}
                <div className="dit-config-column">
                    {/* Combined Avatar & Transcriber Section - Side by Side */}
                    <div className="dit-bot-config-row">
                        {/* Avatar */}
                        <div className="dit-bot-config-item">
                            <div className="dit-bot-config-item-header">
                                <div className="dit-bot-config-icon avatar-icon">
                                    <Users size={16} />
                                </div>
                                <span className="dit-bot-config-item-label">Avatar</span>
                                <button className="dit-config-edit-btn" onClick={() => setShowAvatarModal(true)}>
                                    <Edit2 size={11} />
                                </button>
                            </div>
                            <div className="dit-bot-config-item-content">
                                {uniqueAvatars.length > 0 ? (
                                    uniqueAvatars.map(({ avatar: av, botCount }) => (
                                        <div key={av.id} className="dit-avatar-chip">
                                            <User size={12} />
                                            <span className="dit-avatar-chip-name">{av.name}</span>
                                            {botCount > 1 && <span className="dit-avatar-chip-count">{botCount}</span>}
                                        </div>
                                    ))
                                ) : avatar ? (
                                    <div className="dit-avatar-chip">
                                        <User size={12} />
                                        <span className="dit-avatar-chip-name">{avatar.name}</span>
                                    </div>
                                ) : (
                                    <span className="dit-empty-config">Not set</span>
                                )}
                            </div>
                        </div>

                        {/* Transcriber */}
                        <div className="dit-bot-config-item">
                            <div className="dit-bot-config-item-header">
                                <div className="dit-bot-config-icon transcriber-icon">
                                    <Radio size={16} />
                                </div>
                                <span className="dit-bot-config-item-label">Transcriber</span>
                                <button className="dit-config-edit-btn" onClick={() => setShowTranscriberModal(true)}>
                                    <Edit2 size={11} />
                                </button>
                            </div>
                            <div className="dit-bot-config-item-content">
                                {Object.keys(uniqueTranscribers).length > 1 ? (
                                    Object.entries(uniqueTranscribers).map(([t, sips]) => (
                                        <div key={t} className={`dit-transcriber-chip ${t.toLowerCase().replace('_', '-')}`}>
                                            <span className="dit-transcriber-chip-name">{t}</span>
                                            <span className="dit-transcriber-chip-count">{sips.length}</span>
                                        </div>
                                    ))
                                ) : (
                                    <div className={`dit-transcriber-chip ${currentTranscriber.toLowerCase().replace('_', '-')}`}>
                                        <span className="dit-transcriber-chip-name">{currentTranscriber}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Dialer Config Section */}
                    <div className="dit-config-card">
                        <div className="dit-config-header">
                            <div className="dit-config-title">
                                <Settings size={14} />
                                <span>Dialer Config</span>
                            </div>
                        </div>
                        <div className="dit-config-body dit-dialer-details">
                            <div className="dit-detail dit-detail-sm">
                                <span className="dit-detail-label">Dialer ID</span>
                                <span className="dit-detail-value">{dialer.id}</span>
                            </div>
                            <div className="dit-detail dit-detail-lg">
                                <span className="dit-detail-label">Dialer URL</span>
                                <span className="dit-detail-value dit-url">{bots[0]?.dialer_url || 'Not set'}</span>
                            </div>
                            <div className="dit-detail dit-detail-sm">
                                <span className="dit-detail-label">Campaign</span>
                                <span className="dit-detail-value">{bots[0]?.campaign || 'Not set'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Auto Turn Off Timer Section */}
                    <div className="dit-config-card dit-autologout-card">
                        <div className="dit-config-header">
                            <div className="dit-config-title">
                                <Clock size={14} />
                                <span>Auto Turn Off Timer</span>
                            </div>
                        </div>
                        <div className="dit-config-body">
                            <div className="dit-autologout-row">
                                <div className="dit-autologout-input-group">
                                    <label className="dit-autologout-label">Logout Time (PKT)</label>
                                    <input
                                        type="time"
                                        className="dit-autologout-input"
                                        value={autoLogoutTime}
                                        onChange={(e) => setAutoLogoutTime(e.target.value)}
                                    />
                                </div>
                                <button
                                    className="dit-autologout-save-btn"
                                    onClick={handleAutoLogoutSave}
                                    disabled={autoLogoutSaving || autoLogoutTime === (dialer?.auto_logout_time || '')}
                                >
                                    {autoLogoutSaving ? <Loader2 size={14} className="spinning" /> : <Check size={14} />}
                                    {autoLogoutSaving ? 'Saving...' : 'Save'}
                                </button>
                                {autoLogoutTime && (
                                    <button
                                        className="dit-autologout-clear-btn"
                                        onClick={() => { setAutoLogoutTime(''); }}
                                        title="Clear timer"
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                            <div className="dit-autologout-info">
                                <span className="dit-autologout-current">Current PKT: {getCurrentPKT()}</span>
                                {dialer?.auto_logout_time && (
                                    <span className="dit-autologout-scheduled">
                                        Scheduled: {dialer.auto_logout_time} PKT
                                    </span>
                                )}
                            </div>
                            <p className="dit-autologout-hint">
                                At this time, bots will be logged out. If still active after 1 min, VMs restart with auto-login disabled.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Right Column - Voice Configuration */}
                <div className="dit-config-column">
                    <div className="dit-config-card dit-voice-card">
                        <div className="dit-config-header">
                            <div className="dit-config-title">
                                <Volume2 size={14} />
                                <span>Voice Sets ({voiceSetsInUse.length})</span>
                            </div>
                            <button className="dit-edit-btn" onClick={() => setShowVoiceSetModal(true)}>
                                <Edit2 size={12} /> Manage
                            </button>
                        </div>
                        <div className="dit-config-body">
                            {licenseLoading ? (
                                <div className="dit-loading"><Loader2 size={16} className="spinning" /> Loading...</div>
                            ) : voiceSetsInUse.length > 0 ? (
                                <div className="dit-voice-list">
                                    {voiceSetsInUse.map(vs => (
                                        <div key={vs.id} className="dit-voice-item">
                                            <Volume2 size={14} />
                                            <span className="dit-voice-name">{vs.name}</span>
                                            {vs.gender && <span className="dit-voice-gender">{vs.gender}</span>}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="dit-empty">No voice sets configured</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Avatar Selection Modal */}
            {showAvatarModal && (
                <div className="dit-modal-overlay" onClick={() => setShowAvatarModal(false)}>
                    <div className="dit-modal" onClick={e => e.stopPropagation()}>
                        <div className="dit-modal-header">
                            <h3><Users size={18} /> Select Avatar</h3>
                            <button onClick={() => setShowAvatarModal(false)}><X size={18} /></button>
                        </div>
                        <div className="dit-modal-body">
                            <div className="dit-avatar-grid">
                                {avatars.filter(a => !a.archived).map(a => (
                                    <button
                                        key={a.id}
                                        className={`dit-avatar-option ${avatar?.id === a.id ? 'selected' : ''}`}
                                        onClick={() => handleAvatarChange(a.id)}
                                        disabled={saving}
                                    >
                                        <div className="dit-avatar-option-icon"><User size={20} /></div>
                                        <span className="dit-avatar-option-name">{a.name}</span>
                                        {avatar?.id === a.id && <Check size={14} className="dit-check" />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Voice Set Selection Modal */}
            {showVoiceSetModal && (
                <VoiceSetSelectionModal
                    voiceSets={voiceSets}
                    selectedIds={licenseData?.voice_set_pool || []}
                    onSave={handleVoiceSetChange}
                    onClose={() => setShowVoiceSetModal(false)}
                    saving={saving}
                />
            )}

            {/* Transcriber Selection Modal */}
            {showTranscriberModal && (
                <div className="dit-modal-overlay" onClick={() => setShowTranscriberModal(false)}>
                    <div className="dit-modal dit-modal-sm" onClick={e => e.stopPropagation()}>
                        <div className="dit-modal-header">
                            <h3><Radio size={18} /> Select Transcriber</h3>
                            <button onClick={() => setShowTranscriberModal(false)}><X size={18} /></button>
                        </div>
                        <div className="dit-modal-body">
                            {Object.keys(uniqueTranscribers).length > 1 && (
                                <div className="dit-transcriber-warning">
                                    <AlertTriangle size={14} />
                                    <span>Multiple transcribers in use. Changing will apply to all {bots.length} bots.</span>
                                </div>
                            )}
                            <div className="dit-transcriber-options">
                                {TRANSCRIBER_OPTIONS.map(opt => (
                                    <button
                                        key={opt.value}
                                        className={`dit-transcriber-option ${currentTranscriber === opt.value ? 'selected' : ''}`}
                                        onClick={() => handleTranscriberChange(opt.value)}
                                        disabled={saving}
                                    >
                                        <div className="dit-opt-radio">
                                            {currentTranscriber === opt.value && <Check size={12} />}
                                        </div>
                                        <div className="dit-opt-info">
                                            <span className="dit-opt-label">{opt.label}</span>
                                            <span className="dit-opt-desc">{opt.description}</span>
                                        </div>
                                        {uniqueTranscribers[opt.value] && (
                                            <span className="dit-opt-count">{uniqueTranscribers[opt.value].length} bots</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="dit-modal-footer">
                            <span className="dit-modal-note">Changes apply after VM restart</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

// Voice Set Selection Modal Component
function VoiceSetSelectionModal({ voiceSets, selectedIds, onSave, onClose, saving }) {
    const [selected, setSelected] = useState(new Set(selectedIds))
    const [playingId, setPlayingId] = useState(null)
    const [audioRef] = useState(() => new Audio())

    const toggleVoiceSet = (id) => {
        setSelected(prev => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    const previewVoiceSet = (vs, e) => {
        e.stopPropagation()
        if (playingId === vs.id) {
            audioRef.pause()
            audioRef.src = ''
            setPlayingId(null)
            return
        }
        if (vs.sample_url) {
            setPlayingId(vs.id)
            audioRef.src = vs.sample_url
            audioRef.onended = () => setPlayingId(null)
            audioRef.play().catch(() => setPlayingId(null))
        }
    }

    useEffect(() => {
        return () => {
            audioRef.pause()
            audioRef.src = ''
        }
    }, [])

    return (
        <div className="dit-modal-overlay" onClick={onClose}>
            <div className="dit-modal dit-modal-lg" onClick={e => e.stopPropagation()}>
                <div className="dit-modal-header">
                    <h3><Volume2 size={18} /> Manage Voice Sets</h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                <div className="dit-modal-body">
                    <p className="dit-modal-hint">Select voice sets to rotate through on each call.</p>
                    <div className="dit-voice-grid">
                        {voiceSets.map(vs => (
                            <div
                                key={vs.id}
                                className={`dit-voice-option ${selected.has(vs.id) ? 'selected' : ''}`}
                                onClick={() => toggleVoiceSet(vs.id)}
                            >
                                <button
                                    className="dit-voice-preview"
                                    onClick={(e) => previewVoiceSet(vs, e)}
                                >
                                    {playingId === vs.id ? <Square size={12} /> : <Play size={12} />}
                                </button>
                                <span className="dit-voice-opt-name">{vs.name}</span>
                                {vs.gender && <span className="dit-voice-opt-gender">{vs.gender}</span>}
                                {selected.has(vs.id) && <Check size={14} className="dit-voice-check" />}
                            </div>
                        ))}
                    </div>
                    {selected.size > 0 && (
                        <div className="dit-selected-count">{selected.size} voice set{selected.size > 1 ? 's' : ''} selected</div>
                    )}
                </div>
                <div className="dit-modal-footer">
                    <span className="dit-modal-note">Changes apply after VM restart</span>
                    <div className="dit-modal-actions">
                        <button className="dit-btn secondary" onClick={onClose}>Cancel</button>
                        <button
                            className="dit-btn primary"
                            onClick={() => onSave([...selected])}
                            disabled={saving}
                        >
                            {saving ? <><Loader2 size={14} className="spinning" /> Saving...</> : <><Check size={14} /> Save Changes</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

// ============================================
// DIALER CALL LOGS TAB
// Same as CallLogsTab but filtered for specific dialer
// ============================================
function DialerCallLogsTab({ dialerId, dialerName, dialerBots = [], initialSipFilter = '', onClearFilter, dateFrom, dateTo }) {
    const [logs, setLogs] = useState([])
    const [loading, setLoading] = useState(true)
    const [totalRecords, setTotalRecords] = useState(0)
    const [serverDispoStats, setServerDispoStats] = useState(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(100)
    const [selectedDisposition, setSelectedDisposition] = useState('')
    const [selectedSip, setSelectedSip] = useState(initialSipFilter)
    const [selectedCall, setSelectedCall] = useState(null)
    const [currentlyPlaying, setCurrentlyPlaying] = useState(null)

    // Shared audio ref - persists across re-renders to prevent audio stopping on refresh
    const sharedAudioRef = useRef(null)
    const [audioLoading, setAudioLoading] = useState(false)

    // Initialize shared audio element once
    useEffect(() => {
        if (!sharedAudioRef.current) {
            sharedAudioRef.current = new Audio()
            sharedAudioRef.current.preload = 'none'
        }
        const audio = sharedAudioRef.current

        const handleEnded = () => setCurrentlyPlaying(null)
        const handlePause = () => { setCurrentlyPlaying(null); setAudioLoading(false) }
        const handlePlaying = () => setAudioLoading(false)
        const handleError = () => { setCurrentlyPlaying(null); setAudioLoading(false) }

        audio.addEventListener('ended', handleEnded)
        audio.addEventListener('pause', handlePause)
        audio.addEventListener('playing', handlePlaying)
        audio.addEventListener('error', handleError)

        return () => {
            audio.removeEventListener('ended', handleEnded)
            audio.removeEventListener('pause', handlePause)
            audio.removeEventListener('playing', handlePlaying)
            audio.removeEventListener('error', handleError)
            audio.pause()
        }
    }, [])

    // Play/pause recording using shared audio element
    const toggleRecording = useCallback(async (src) => {
        const audio = sharedAudioRef.current
        if (!audio || !src) return

        if (currentlyPlaying === src) {
            // Currently playing this one - pause it
            audio.pause()
            setCurrentlyPlaying(null)
        } else {
            // Play new recording
            audio.pause()
            audio.src = src
            setAudioLoading(true)
            try {
                await audio.play()
                setCurrentlyPlaying(src)
            } catch (err) {
                console.error('Playback error:', err)
                setAudioLoading(false)
                setCurrentlyPlaying(null)
            }
        }
    }, [currentlyPlaying])

    // Search state
    const [searchQuery, setSearchQuery] = useState('')
    const [searchResults, setSearchResults] = useState(null)
    const [searchMeta, setSearchMeta] = useState(null)
    const [isSearching, setIsSearching] = useState(false)
    const searchTimeout = useRef(null)

    // Update selectedSip when initialSipFilter changes (from bot action)
    useEffect(() => {
        if (initialSipFilter) {
            setSelectedSip(initialSipFilter)
        }
    }, [initialSipFilter])

    const PAGE_SIZES = [50, 100, 250, 500]

    // Get unique SIPs from dialer bots
    const availableSips = useMemo(() => {
        const sips = dialerBots
            .map(b => b.sip_id)
            .filter((v, i, a) => v && a.indexOf(v) === i)
            .sort()
        return sips.map(sip => ({ id: sip, name: sip }))
    }, [dialerBots])

    // Disposition config - same as CallLogsTab
    const DISPOSITION_CONFIG = {
        'transfer-call': { label: 'Transfer', color: '#10b981', icon: PhoneForwarded },
        'call-transfered': { label: 'Transferred', color: '#10b981', icon: PhoneForwarded },
        'TRANSFER': { label: 'Transfer', color: '#10b981', icon: PhoneForwarded },
        'not-interested': { label: 'Not Interested', color: '#f59e0b', icon: PhoneOff },
        'not_interested': { label: 'Not Interested', color: '#f59e0b', icon: PhoneOff },
        'do-not-call': { label: 'DNC', color: '#ef4444', icon: Ban },
        'do_not_call': { label: 'DNC', color: '#ef4444', icon: Ban },
        'answering-machine': { label: 'Voicemail', color: '#8b5cf6', icon: Voicemail },
        'answering_machine': { label: 'Voicemail', color: '#8b5cf6', icon: Voicemail },
        'no-answer': { label: 'No Answer', color: '#6b7280', icon: PhoneOff },
        'no_answer': { label: 'No Answer', color: '#6b7280', icon: PhoneOff },
        'call-back': { label: 'Callback', color: '#3b82f6', icon: Clock },
        'call_back': { label: 'Callback', color: '#3b82f6', icon: Clock },
    }

    const getDispoConfig = (d) => DISPOSITION_CONFIG[d] || { label: d || 'Unknown', color: '#6b7280', icon: Phone }

    // Fetch logs with server-side pagination
    const fetchLogs = useCallback(async (page = 1) => {
        if (!dialerId) return
        setLoading(true)
        try {
            const offset = (page - 1) * pageSize
            const params = {
                limit: pageSize,
                offset,
                dialer_id: dialerId,
                from: dateFrom,
                to: dateTo
            }
            if (selectedDisposition) params.disposition = selectedDisposition
            if (selectedSip) params.sip_id = selectedSip

            const res = await monitoringApi.getDispositions(params)
            const data = res.data?.data || res.data || []
            const total = res.data?.total || data.length

            setLogs(data)
            setTotalRecords(total)
        } catch (err) {
            console.error('Failed to fetch call logs:', err)
            setLogs([])
            setTotalRecords(0)
        } finally {
            setLoading(false)
        }
    }, [dialerId, pageSize, selectedDisposition, selectedSip, dateFrom, dateTo])

    // Fetch disposition stats from server (covers ALL matching records, not just current page)
    const fetchDispoStats = useCallback(async () => {
        if (!dialerId) return
        try {
            const params = { dialer_id: dialerId, from: dateFrom, to: dateTo }
            if (selectedSip) params.sip_id = selectedSip
            const res = await monitoringApi.getDispositionStats(params)
            setServerDispoStats(res.data?.dispositions || [])
        } catch (err) {
            console.error('Failed to fetch disposition stats:', err)
            setServerDispoStats(null)
        }
    }, [dialerId, selectedSip, dateFrom, dateTo])

    // Reset and fetch when filters change
    useEffect(() => {
        setCurrentPage(1)
        setSearchQuery('')
        setSearchResults(null)
        setSearchMeta(null)
        fetchLogs(1)
        fetchDispoStats()
    }, [dialerId, selectedDisposition, selectedSip, pageSize, dateFrom, dateTo])

    // Fetch when page changes (non-search mode)
    useEffect(() => {
        if (searchResults === null) {
            fetchLogs(currentPage)
        }
    }, [currentPage])

    // Debounced search
    useEffect(() => {
        if (searchTimeout.current) clearTimeout(searchTimeout.current)

        if (!searchQuery || searchQuery.trim().length < 2) {
            setSearchResults(null)
            setSearchMeta(null)
            setIsSearching(false)
            return
        }

        setIsSearching(true)
        searchTimeout.current = setTimeout(async () => {
            try {
                const res = await monitoringApi.search({
                    q: searchQuery.trim(),
                    dialer_id: dialerId,
                    start_date: dateFrom,
                    end_date: dateTo,
                    limit: 500
                })

                const data = res.data?.data || []
                const meta = res.data?.meta || {}

                // Filter by SIP if selected
                const filtered = selectedSip
                    ? data.filter(c => c.sip_id === selectedSip)
                    : data

                setSearchResults(filtered)
                setSearchMeta(meta)
                setCurrentPage(1)
            } catch (err) {
                console.error('[Search] Error:', err)
                setSearchResults([])
                setSearchMeta({ error: true })
            } finally {
                setIsSearching(false)
            }
        }, 500)

        return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current) }
    }, [searchQuery, dialerId, dateFrom, dateTo, selectedSip])

    // Determine data source
    const isInSearchMode = searchResults !== null
    const displayLogs = isInSearchMode ? searchResults : logs
    const displayTotal = isInSearchMode ? searchResults.length : totalRecords

    // Client-side pagination for search results
    const paginatedLogs = isInSearchMode
        ? displayLogs.slice((currentPage - 1) * pageSize, currentPage * pageSize)
        : displayLogs

    const totalPages = Math.ceil(displayTotal / pageSize)

    // Disposition stats: use server-side stats (all records), fall back to client-side for search mode
    const dispositionStats = useMemo(() => {
        if (!isInSearchMode && serverDispoStats) {
            return serverDispoStats.map(d => ({ disposition: d.disposition, count: d.count }))
        }
        const stats = {}
        displayLogs.forEach(c => {
            const d = c.disposition || 'unknown'
            stats[d] = (stats[d] || 0) + 1
        })
        return Object.entries(stats)
            .map(([disposition, count]) => ({ disposition, count }))
            .sort((a, b) => b.count - a.count)
    }, [isInSearchMode, serverDispoStats, displayLogs])

    // Recording count
    const recordingCount = useMemo(() => displayLogs.filter(c => c.recording_url).length, [displayLogs])

    // Helpers
    const formatDuration = (s) => s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : '0:00'
    const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-US', {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    }) : '-'

    // Clear search
    const clearSearch = () => {
        setSearchQuery('')
        setSearchResults(null)
        setSearchMeta(null)
        setCurrentPage(1)
    }

    // Recording player - uses shared audio element to prevent stopping on refresh
    const RecordingPlayer = ({ src, duration }) => {
        const isPlaying = currentlyPlaying === src
        const isLoading = audioLoading && currentlyPlaying === src

        const handleClick = (e) => {
            e.stopPropagation()
            toggleRecording(src)
        }

        if (!src) return <span className="cl-no-recording">—</span>

        return (
            <button
                className={`cl-play-btn ${isPlaying ? 'playing' : ''} ${isLoading ? 'loading' : ''}`}
                onClick={handleClick}
                title={duration ? `${duration}s recording` : 'Play recording'}
            >
                {isLoading ? (
                    <span className="cl-play-spinner" />
                ) : isPlaying ? (
                    <Pause size={14} />
                ) : (
                    <Play size={14} />
                )}
                {duration > 0 && <span className="cl-play-duration">{duration}s</span>}
            </button>
        )
    }

    // Get row class based on disposition
    const getRowClass = (disposition) => {
        const dispo = (disposition || '').toLowerCase()
        if (dispo.includes('transfer')) return 'dispo-transfer'
        if (dispo.includes('answering') || dispo.includes('voicemail')) return 'dispo-voicemail'
        if (dispo.includes('not-interested') || dispo.includes('not_interested')) return 'dispo-not-interested'
        if (dispo.includes('do-not-call') || dispo.includes('do_not_call') || dispo.includes('dnc')) return 'dispo-dnc'
        if (dispo.includes('no-answer') || dispo.includes('no_answer')) return 'dispo-no-answer'
        if (dispo.includes('call-back') || dispo.includes('call_back') || dispo.includes('callback')) return 'dispo-callback'
        return ''
    }

    return (
        <div className="dcl-container">
            {/* Toolbar Row - Search and SIP filter */}
            <div className="dcl-toolbar">
                <div className={`dcl-search ${isInSearchMode ? 'active' : ''}`}>
                    <Search size={14} />
                    <input
                        type="text"
                        placeholder="Search phone or transcript..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button className="dcl-search-clear" onClick={clearSearch}><X size={12} /></button>
                    )}
                </div>

                {availableSips.length > 0 && (
                    <select
                        value={selectedSip}
                        onChange={(e) => setSelectedSip(e.target.value)}
                        className="dcl-select"
                    >
                        <option value="">All SIPs ({availableSips.length})</option>
                        {availableSips.map(sip => (
                            <option key={sip.id} value={sip.id}>{sip.name}</option>
                        ))}
                    </select>
                )}

                {isInSearchMode && searchMeta && (
                    <div className="dcl-search-badge">
                        {searchMeta.total_matches || 0} matches
                        <button onClick={clearSearch}><X size={10} /></button>
                    </div>
                )}

                <button className="dcl-refresh-btn" onClick={() => fetchLogs(currentPage)} disabled={loading}>
                    <RefreshCw size={14} className={loading ? 'spinning' : ''} />
                </button>
            </div>

            {/* Combined Disposition + Pagination Row */}
            <div className="dcl-filter-row">
                <div className="dcl-dispo-chips">
                    {dispositionStats.slice(0, 6).map((stat, i) => {
                        const config = getDispoConfig(stat.disposition)
                        const Icon = config.icon
                        const isActive = selectedDisposition === stat.disposition
                        return (
                            <button
                                key={i}
                                className={`dcl-chip ${isActive ? 'selected' : ''}`}
                                style={{ '--accent': config.color }}
                                onClick={() => setSelectedDisposition(isActive ? '' : stat.disposition)}
                            >
                                <Icon size={12} />
                                <span className="dcl-chip-count">{stat.count}</span>
                                <span className="dcl-chip-label">{config.label}</span>
                            </button>
                        )
                    })}
                </div>

                {totalPages > 0 && !loading && !isSearching && (
                    <div className="dcl-page-compact">
                        <span className="dcl-page-info">{((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, displayTotal)} of {displayTotal.toLocaleString()}</span>
                        <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1) }}>
                            {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>First</button>
                        <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}><ChevronLeft size={12} /></button>
                        <span>{currentPage}/{totalPages}</span>
                        <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}><ChevronRight size={12} /></button>
                        <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>Last</button>
                    </div>
                )}
            </div>

            {/* Table */}
            <div className="dcl-table-wrapper">
                {(loading || isSearching) ? (
                    <div className="dcl-loading">
                        <RefreshCw size={20} className="spinning" />
                        <span>{isSearching ? 'Searching...' : 'Loading...'}</span>
                    </div>
                ) : paginatedLogs.length === 0 ? (
                    <div className="dcl-empty">
                        <Phone size={32} />
                        <p>{isInSearchMode ? 'No matches found' : 'No calls found'}</p>
                    </div>
                ) : (
                    <table className="dcl-table">
                        <thead>
                            <tr>
                                <th>TIME</th>
                                <th>PHONE</th>
                                <th>DISPOSITION</th>
                                <th>DURATION</th>
                                <th>RECORDING</th>
                                <th>HUNG UP</th>
                                <th>VOICE SET</th>
                                <th>STEP</th>
                                <th>SIP</th>
                                {isInSearchMode && <th>MATCH</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedLogs.map((call, i) => {
                                const config = getDispoConfig(call.disposition)
                                const Icon = config.icon
                                const rowClass = getRowClass(call.disposition)

                                return (
                                    <tr
                                        key={call.id || call.call_id || i}
                                        className={`${rowClass} ${currentlyPlaying === call.recording_url ? 'playing' : ''}`}
                                        onClick={() => setSelectedCall(call)}
                                    >
                                        <td className="dcl-td-time">{formatDateTime(call.call_end)}</td>
                                        <td className="dcl-td-phone">{call.phone_number || call.phone_masked || '-'}</td>
                                        <td>
                                            <span className="dcl-dispo-badge" style={{ '--c': config.color }}>
                                                <Icon size={12} />
                                                {config.label}
                                            </span>
                                        </td>
                                        <td className="dcl-td-dur">{formatDuration(call.call_duration)}</td>
                                        <td className="dcl-td-rec">
                                            <RecordingPlayer src={call.recording_url} duration={call.recording_duration} />
                                        </td>
                                        <td className="dcl-td-hungup">
                                            {call.hung_up_by === 'caller' ? (
                                                <span className="dcl-hungup caller">Caller</span>
                                            ) : call.hung_up_by === 'agent' ? (
                                                <span className="dcl-hungup agent">Agent</span>
                                            ) : (
                                                <span className="dcl-hungup">—</span>
                                            )}
                                        </td>
                                        <td className="dcl-td-voiceset">{call.voice_set_name || call.voice_set || '-'}</td>
                                        <td className="dcl-td-step">{call.script_step_reached || '-'}</td>
                                        <td className="dcl-td-sip">{call.sip_id || '-'}</td>
                                        {isInSearchMode && (
                                            <td><span className={`dcl-match ${call.match_source}`}>
                                                {call.match_source === 'transcription' ? '📝' : call.match_source === 'both' ? '✓✓' : '✓'}
                                            </span></td>
                                        )}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Call Detail Modal */}
            {selectedCall && (
                <CallDetailModal
                    callId={selectedCall.call_id}
                    disposition={selectedCall}
                    onClose={() => setSelectedCall(null)}
                />
            )}
        </div>
    )
}

// VMs Tab Component
function VMsTab({ vms, loading, dialerBots, vmActionLoading, onVMAction, onRefresh, timeAgo }) {
    if (loading) {
        return (
            <div className="vms-tab-loading">
                <RefreshCw className="spin" size={24} />
                <span>Loading VMs...</span>
            </div>
        )
    }

    return (
        <div className="vms-tab">
            <div className="vms-toolbar">
                <div className="vms-toolbar-left">
                    <span className="vm-count">{vms.length} VMs linked to this dialer</span>
                </div>
                <div className="vms-toolbar-right">
                    <button className="btn btn-secondary btn-sm" onClick={onRefresh}>
                        <RefreshCw size={14} />
                        Refresh
                    </button>
                </div>
            </div>

            <div className="vms-table-container">
                <table className="vms-table">
                    <thead>
                        <tr>
                            <th>VMID</th>
                            <th>Name</th>
                            <th>Server</th>
                            <th>Status</th>
                            <th>CPU%</th>
                            <th>RAM</th>
                            <th>Linked SIP</th>
                            <th className="actions-col">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {vms.length === 0 ? (
                            <tr>
                                <td colSpan="8" className="empty-row">No VMs linked to this dialer</td>
                            </tr>
                        ) : (
                            vms.map(vm => {
                                const linkedBot = dialerBots.find(b => b.vmid === vm.vmid && b.server_ip === vm.server_ip)
                                return (
                                    <tr key={`${vm.server_ip}-${vm.vmid}`}>
                                        <td className="vmid">{vm.vmid}</td>
                                        <td>{vm.name}</td>
                                        <td className="server-cell">{vm.server_name || vm.server_ip}</td>
                                        <td>
                                            <span className={`status-badge ${vm.status}`}>
                                                {vm.status === 'running' ? <Wifi size={12} /> : <WifiOff size={12} />}
                                                {vm.status}
                                            </span>
                                        </td>
                                        <td>{vm.cpu ? `${Math.round(vm.cpu * 100)}%` : '-'}</td>
                                        <td>{vm.maxmem ? `${Math.round(vm.mem / vm.maxmem * 100)}%` : '-'}</td>
                                        <td>{linkedBot?.sip_id || '-'}</td>
                                        <td className="actions-col">
                                            <div className="action-buttons">
                                                {vm.status === 'running' ? (
                                                    <>
                                                        <button
                                                            className="action-btn stop"
                                                            onClick={() => onVMAction(vm, 'stop')}
                                                            disabled={vmActionLoading[vm.vmid]}
                                                            title="Stop"
                                                        >
                                                            {vmActionLoading[vm.vmid] === 'stop' ? (
                                                                <RefreshCw size={12} className="spin" />
                                                            ) : (
                                                                <Square size={12} />
                                                            )}
                                                        </button>
                                                        <button
                                                            className="action-btn restart"
                                                            onClick={() => onVMAction(vm, 'reboot')}
                                                            disabled={vmActionLoading[vm.vmid]}
                                                            title="Reboot"
                                                        >
                                                            {vmActionLoading[vm.vmid] === 'reboot' ? (
                                                                <RefreshCw size={12} className="spin" />
                                                            ) : (
                                                                <RotateCcw size={12} />
                                                            )}
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button
                                                        className="action-btn start"
                                                        onClick={() => onVMAction(vm, 'start')}
                                                        disabled={vmActionLoading[vm.vmid]}
                                                        title="Start"
                                                    >
                                                        {vmActionLoading[vm.vmid] === 'start' ? (
                                                            <RefreshCw size={12} className="spin" />
                                                        ) : (
                                                            <Play size={12} />
                                                        )}
                                                    </button>
                                                )}
                                                {vm.novnc_port && vm.server_ip && !/[a-zA-Z]/.test(vm.server_ip) && (
                                                    <button
                                                        className="action-btn"
                                                        onClick={() => window.open(`http://${vm.server_ip}:${vm.novnc_port}/vnc.html?autoconnect=true&resize=remote`, '_blank')}
                                                        title="Open VNC"
                                                    >
                                                        <Monitor size={12} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}
